import type { FieldConfig, StepConfig, StepStatus } from './VerticalStepForm.model';
import type { FieldErrors } from 'react-hook-form';
import { _get, _notNil } from '@/littledash';

export const stepHasErrors = (fields: Array<FieldConfig>, errors: FieldErrors): boolean => {
  return fields.some((field) => _notNil(_get(errors, field.name)));
};

const isSelectOption = (value: unknown): value is { label: string; value: string } =>
  typeof value === 'object' && _notNil(value) && 'value' in value;

/**
 * Select fields hold the full { label, value } option in form state so the
 * label survives step collapse/reopen. This flattens each select field to its
 * primitive value for the submit payload, leaving non-select fields untouched.
 */
export const flattenSelectValues = <T extends Record<string, unknown>>(values: T, steps: Array<StepConfig>): T => {
  const selectFieldNames = steps.flatMap((step) => step.fields).filter((field) => field.type === 'select');
  return selectFieldNames.reduce<Record<string, unknown>>(
    (result, field) => {
      const current = result[field.name];
      return isSelectOption(current) ? { ...result, [field.name]: current.value } : result;
    },
    { ...values }
  ) as T;
};

export type StepVariant = 'error' | 'completed' | 'in_progress' | 'pending';

export const getStepVariant = (status: StepStatus, hasErrors: boolean): StepVariant => {
  if (hasErrors) {
    return 'error';
  }
  if (status === 'completed') {
    return 'completed';
  }
  if (status === 'in_progress') {
    return 'in_progress';
  }
  return 'pending';
};
